import { useEffect, useRef, useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { f2 } from '../../copyFlow2'
import { CATS, type Card, type Tile } from '../../lib/browse'
import { hasF2 } from '../../copyFlow2'
import { categoryText } from '../../ui/CategoryLabel'
import { IconButton } from '../../ui/Button'
import { Arrow } from '../../ui/icons'
import { ListingCard } from './ListingCard'
import { photoRow } from './Photo'
import { groupRepeats } from '../../lib/repeats'
import { Symbol } from '../../ui/Symbol'
import { COLLECTIONS } from '../../lib/collections'
import { mood } from '../../copyMoods'

/** A category's name as the tiles show it (the copy file's category.* names: Classes, Markets, Day trips). */
export const catName = (cat: string) => {
  const key = `category.${cat.replace(/-/g, '_')}`
  return hasF2(key) ? f2(key) : categoryText(cat)
}

const countLabel = (n: number) => (n === 1 ? f2('browse.category_count_one') : f2('browse.category_count_other', { count: n.toLocaleString() }))
export const resultsLabel = (n: number) => (n === 1 ? f2('browse.results_one') : f2('browse.results_other', { count: n.toLocaleString() }))

/**
 * B-154 (Alvin pin 560): with a mouse, a row can be dragged sideways like on a phone. Touch keeps native scrolling.
 * A press that moves less than 6 px is still a click; a real drag swallows the click that follows, so letting go over a
 * card never opens it. Snapping is off while dragging and comes back after, so the row settles on a card.
 */
export function dragScroll(el: HTMLElement): () => void {
  let start: { x: number; left: number; id: number } | null = null
  let moved = false
  const down = (e: PointerEvent) => {
    if (e.pointerType !== 'mouse' || e.button !== 0 || el.scrollWidth <= el.clientWidth + 1) return
    if ((e.target as Element).closest('button, input, select, textarea, [role="slider"]')) return
    start = { x: e.clientX, left: el.scrollLeft, id: e.pointerId }
    moved = false
  }
  const move = (e: PointerEvent) => {
    if (!start || e.pointerId !== start.id) return
    const dx = e.clientX - start.x
    if (!moved && Math.abs(dx) < 6) return
    if (!moved) {
      moved = true
      el.style.scrollSnapType = 'none'
      el.style.cursor = 'grabbing'
      el.style.userSelect = 'none'
      try { el.setPointerCapture(start.id) } catch { /* the pointer may already be gone */ }
    }
    el.scrollLeft = start.left - dx
  }
  const up = (e: PointerEvent) => {
    if (!start || e.pointerId !== start.id) return
    start = null
    if (!moved) return
    const left = el.scrollLeft
    el.style.scrollSnapType = ''
    el.style.cursor = ''
    el.style.userSelect = ''
    el.scrollLeft = left // restoring snap can jump; settle from where the drag ended
  }
  const click = (e: MouseEvent) => {
    if (moved) { e.preventDefault(); e.stopPropagation(); moved = false }
  }
  const dragstart = (e: DragEvent) => { if (start) e.preventDefault() } // links and photos would start a native drag
  el.addEventListener('pointerdown', down)
  el.addEventListener('pointermove', move)
  el.addEventListener('pointerup', up)
  el.addEventListener('pointercancel', up)
  el.addEventListener('click', click, true)
  el.addEventListener('dragstart', dragstart)
  return () => {
    el.removeEventListener('pointerdown', down)
    el.removeEventListener('pointermove', move)
    el.removeEventListener('pointerup', up)
    el.removeEventListener('pointercancel', up)
    el.removeEventListener('click', click, true)
    el.removeEventListener('dragstart', dragstart)
  }
}

/** Previous / next for a sideways rail: scroll by one page; each button is off at its end. */
function useRail() {
  const ref = useRef<HTMLDivElement>(null)
  const [edge, setEdge] = useState({ start: true, end: false })
  useEffect(() => {
    const el = ref.current
    if (!el) return
    const update = () => setEdge({ start: el.scrollLeft < 4, end: el.scrollLeft + el.clientWidth > el.scrollWidth - 4 })
    update()
    el.addEventListener('scroll', update, { passive: true })
    const ro = new ResizeObserver(update)
    ro.observe(el)
    const stopDrag = dragScroll(el)
    return () => {
      el.removeEventListener('scroll', update)
      ro.disconnect()
      stopDrag()
    }
  }, [])
  const page = (dir: 1 | -1) => {
    const el = ref.current
    if (!el) return
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    el.scrollBy({ left: dir * el.clientWidth * 0.9, behavior: reduce ? 'auto' : 'smooth' })
  }
  return { ref, edge, page }
}

function RailArrows({ edge, page, className = '' }: { edge: { start: boolean; end: boolean }; page: (d: 1 | -1) => void; className?: string }) {
  if (edge.start && edge.end) return null
  return (
    <div className={`flex items-center gap-1 ${className}`}>
      <IconButton label={f2('browse.previous')} disabled={edge.start} onClick={() => page(-1)} className="bg-surface ring-1 ring-inset ring-line">
        <Arrow dir="left" size={18} />
      </IconButton>
      <IconButton label={f2('browse.next')} disabled={edge.end} onClick={() => page(1)} className="bg-surface ring-1 ring-inset ring-line">
        <Arrow dir="right" size={18} />
      </IconButton>
    </div>
  )
}

/** The category mark: the Field signals v2 symbol (SYMBOLS.md), Graphite, decoration only. */
export function CatMark({ cat, size = 40 }: { cat: string; size?: number }) {
  return <Symbol cat={cat} size={size} />
}

/** Sixteen category tiles: a two-row sideways rail on a phone, eight by two on a desktop. Zero counts still link. */
export function Tiles({ tiles, search, loading, title }: { tiles: Tile[] | null; search: string; loading: boolean; title?: string }) {
  const { ref, edge, page } = useRail()
  const counts = new Map((tiles ?? []).map((t) => [t.cat, t.count]))
  return (
    <section data-review-id="tiles" aria-labelledby="cats-title">
      <div className="flex items-center justify-between gap-3">
        <h2 id="cats-title" className="t-section">{title ?? f2('browse.categories')}</h2>
        <RailArrows edge={edge} page={page} className="lg:hidden" />
      </div>
      <div ref={ref} data-review-id="tiles-grid"
        className="-mx-5 mt-4 grid snap-x snap-mandatory grid-cols-[repeat(8,169px)] grid-rows-2 gap-3 overflow-x-auto scroll-px-5 px-5 pb-1 [scrollbar-width:none] md:-mx-8 md:scroll-px-8 md:px-8 lg:mx-0 lg:grid-cols-8 lg:gap-4 lg:overflow-visible lg:px-0 [&::-webkit-scrollbar]:hidden">
        {CATS.map((cat, i) => {
          const n = counts.get(cat)
          return (
            // read across each row: Art, Film, Music ... on the first row, Markets ... Other on the second
            <Link key={cat} to={{ pathname: `/category/${cat}`, search }} data-cat={cat} data-review-id={`tile:${cat}`} style={{ gridColumn: (i % 8) + 1, gridRow: Math.floor(i / 8) + 1 }}
              className="gh-cattile press press-card flex h-[144px] snap-start flex-col justify-between border border-line bg-surface p-4 text-ink hover:border-ink-2">
              <CatMark cat={cat} size={40} />
              <span className="mt-3 block">
                <span className="block text-[21px] font-semibold leading-[25px] tracking-[-0.2px] text-ink">{catName(cat)}</span>
                <span className="t-caption mt-0.5 block text-ink-2" aria-live="off">
                  {n !== undefined ? countLabel(n) : loading ? ' ' : countLabel(0)}
                </span>
              </span>
            </Link>
          )
        })}
      </div>
    </section>
  )
}

/** A mood's mark: a Field signals v2 file by name (collections.ts `sym`), Graphite, decoration only. */
export function MoodMark({ sym, size = 40 }: { sym: string; size?: number }) {
  return <span aria-hidden="true" className="sym" style={{ width: size, height: size, ['--sym' as string]: `url("/symbols/field-signals/${sym}.svg")` }} />
}

/** R-60: the seven mood collections (Alvin 10/02), in place of the category tiles: one compact sideways rail on a phone
 *  (about 120 px tall, so the ranked rows still start within one scroll), seven across on a desktop. Each opens
 *  /mood/:key, the full list in the person's own order; the subtitle shows on that page. Zero counts still link. */
export function Moods({ counts, search, loading, title }: { counts: { key: string; count: number }[] | null; search: string; loading: boolean; title?: string }) {
  const { ref, edge, page } = useRail()
  const n = new Map((counts ?? []).map((c) => [c.key, c.count]))
  return (
    <section data-review-id="moods" aria-labelledby="moods-title">
      <div className="flex items-center justify-between gap-3">
        <h2 id="moods-title" className="t-section">{title ?? mood('mood.title')}</h2>
        <RailArrows edge={edge} page={page} className="lg:hidden" />
      </div>
      <div ref={ref} data-review-id="moods-grid"
        className="-mx-5 mt-3 grid snap-x snap-mandatory grid-flow-col auto-cols-[136px] grid-rows-1 gap-2 overflow-x-auto scroll-px-5 px-5 pb-1 [scrollbar-width:none] md:-mx-8 md:scroll-px-8 md:px-8 lg:mx-0 lg:grid-flow-row lg:grid-cols-7 lg:gap-4 lg:overflow-visible lg:px-0 [&::-webkit-scrollbar]:hidden">
        {COLLECTIONS.map((c) => {
          const count = n.get(c.key)
          return (
            <Link key={c.key} to={{ pathname: `/mood/${c.key}`, search }} data-mood={c.key} data-review-id={`mood:${c.key}`}
              className="gh-mood press press-card flex min-h-[112px] snap-start flex-col justify-between border border-line bg-surface p-3 text-ink hover:border-ink-2">
              <MoodMark sym={c.sym} size={24} />
              <span className="mt-2 block">
                <span className="block text-[16px] font-semibold leading-[20px] text-ink">{mood(`mood.${c.key}`)}</span>
                <span className="t-caption mt-0.5 block text-ink-2" aria-live="off">
                  {count !== undefined ? countLabel(count) : loading ? ' ' : countLabel(0)}
                </span>
              </span>
            </Link>
          )
        })}
      </div>
    </section>
  )
}

/** One discovery row: title + See all, then up to six cards (a peeking rail on a phone, three across with arrows on a desktop). */
export function Row({ id, title, note = null, cards, seeAll, showDistance, reason, isNew }: { id: string; title: string; note?: string | null; cards: Card[]; seeAll: { pathname: string; search: string } | null; showDistance: boolean; reason?: (c: Card) => string | null; isNew?: (c: Card) => boolean }) {
  const { ref, edge, page } = useRail()
  const withPhotos = photoRow(cards)
  return (
    <section data-review-id={`row:${id}`} aria-labelledby={`row-${id}`}>
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <h2 id={`row-${id}`} className="t-section">{title}</h2>
          {note && <p data-review-id={`row-note:${id}`} className="t-meta mt-1 text-ink-2">{note}</p>}
        </div>
        <div className="flex items-center gap-2">
          {seeAll && (
            <Link to={seeAll} aria-label={f2('browse.see_all_label', { row: title })} className="press t-control inline-flex min-h-11 items-center rounded-[12px] px-2 text-accent-ink">
              {f2('browse.see_all')}
            </Link>
          )}
          <RailArrows edge={edge} page={page} className="hidden lg:flex" />
        </div>
      </div>
      <div ref={ref}
        className="-mx-5 mt-4 flex snap-x snap-mandatory gap-4 overflow-x-auto scroll-px-5 px-5 pb-1 [scrollbar-width:none] md:-mx-8 md:scroll-px-8 md:px-8 lg:mx-0 lg:gap-6 lg:px-0 lg:scroll-px-0 [&::-webkit-scrollbar]:hidden">
        {groupRepeats(cards).map(({ card: c, more }, i) => (
          <ListingCard key={c.item.id} card={c} showDistance={showDistance} reason={reason?.(c) ?? null} more={more} photo={withPhotos} row={id} position={i}
            className={`w-[326px] shrink-0 snap-start lg:w-[calc((100%-48px)/3)]${isNew?.(c) ? ' mg-slide-in' : ''}`} />
        ))}
      </div>
    </section>
  )
}

/** The full list: one column on a phone, three on a desktop (row-major). */
export function Grid({ children }: { children: ReactNode }) {
  return <div className="grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-6 lg:grid-cols-3">{children}</div>
}
